import { create } from "zustand";
import { appointmentsApi } from "@services/api/appointments";
import {
  Appointment,
  AppointmentCategory,
  CategoryConfig,
  WorkshopAvailability,
} from "../types/garage/planning";

export const CATEGORIES: CategoryConfig[] = [
  { id: "rdv_divers", label: "RDV Divers", color: "#818CF8", bgColor: "#E0E6FF", borderColor: "#CCD0FF", barColor: "#A5B4FC" },
  { id: "location_rac", label: "Location", color: "#5C6BFF", bgColor: "#CACFFF", borderColor: "#B7BDFF", barColor: "#818CF8" },
  { id: "vehicule_pret", label: "Véhicule de prêt", color: "#6F61FF", bgColor: "#D8D4FF", borderColor: "#C5BFFF", barColor: "#9B8FFC" },
  { id: "carrosserie", label: "Carrosserie", color: "#8258FF", bgColor: "#DDD2FF", borderColor: "#CDBDFF", barColor: "#A78BFA" },
  { id: "reception_1", label: "Réception 1", color: "#CC5EFF", bgColor: "#EFD9FF", borderColor: "#E6C0FF", barColor: "#CC5EFF" },
  { id: "reception_2", label: "Réception 2", color: "#DF41FF", bgColor: "#EFC7FF", borderColor: "#E9B9FF", barColor: "#DF41FF" },
];

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface PlanningState {
  selectedDate: string;
  viewMode: "jour" | "semaine";
  activeCategories: AppointmentCategory[];
  appointments: Appointment[];
  workshopAvailability: WorkshopAvailability[];
  isLoading: boolean;
  isRefreshing: boolean;
  searchQuery: string;
  _fetchedOrgId: string | undefined;

  fetchAppointments: (orgId: string) => Promise<void>;
  setSelectedDate: (date: string) => void;
  setViewMode: (mode: "jour" | "semaine") => void;
  toggleCategory: (cat: AppointmentCategory) => void;
  setSearchQuery: (query: string) => void;
  getFilteredAppointments: () => Appointment[];
  updateAppointment: (id: string, updates: Partial<Pick<Appointment, "startHour" | "durationHours" | "category" | "date">>) => void;
}

export const usePlanningStore = create<PlanningState>((set, get) => ({
  selectedDate: todayIso(),
  viewMode: "jour",
  activeCategories: [
    "rdv_divers",
    "location_rac",
    "carrosserie",
    "reception_1",
    "reception_2",
  ],
  appointments: [],
  workshopAvailability: [],
  isLoading: false,
  isRefreshing: false,
  searchQuery: "",
  _fetchedOrgId: undefined,

  fetchAppointments: async (orgId: string) => {
    const state = get();
    if (state.isLoading || state.isRefreshing) return;
    const showLoading = state.appointments.length === 0;
    set({ isLoading: showLoading, isRefreshing: true, _fetchedOrgId: orgId });
    try {
      const appointments = await appointmentsApi.list({ org_id: orgId });
      set({ appointments, isLoading: false, isRefreshing: false });
    } catch (error) {
      console.error("Error fetching appointments:", error);
      set({ appointments: [], isLoading: false, isRefreshing: false, _fetchedOrgId: undefined });
    }
  },

  setSelectedDate: (date) => set({ selectedDate: date }),
  setViewMode: (mode) => set({ viewMode: mode }),
  toggleCategory: (cat) =>
    set((state) => ({
      activeCategories: state.activeCategories.includes(cat)
        ? state.activeCategories.filter((c) => c !== cat)
        : [...state.activeCategories, cat],
    })),
  setSearchQuery: (query) => set({ searchQuery: query }),

  updateAppointment: (id, updates) => {
    set((state) => ({
      appointments: state.appointments.map((a) =>
        a.id === id ? { ...a, ...updates } : a
      ),
    }));
    appointmentsApi.update(id, updates as Partial<Appointment>).catch((e) => console.error("Error updating appointment:", e));
  },

  getFilteredAppointments: () => {
    const state = get();
    return state.appointments.filter(
      (a) =>
        a.date === state.selectedDate &&
        state.activeCategories.includes(a.category) &&
        (state.searchQuery === "" ||
          a.clientName.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
          a.plate.toLowerCase().includes(state.searchQuery.toLowerCase()))
    );
  },
}));
